import React from 'react';

function ProductModal({ product, distributorDetails, loadingDistributor, inventoryDetails, loadingInventory, onClose }) {
  const {
    product_id,
    name,
    manufacturers,
    salt_composition,
    medicine_type,
    introduction,
    benefits,
    description,
    how_to_use,
    safety_advise,
    if_miss,
    packaging_detail,
    mrp,
    prescription_required,
    fact_box,
    primary_use,
    storage,
    common_side_effect,
    alcohol_interaction,
    pregnancy_interaction,
    lactation_interaction,
    driving_interaction,
    kidney_interaction,
    liver_interaction,
    manufacturer_address,
    q_a,
    how_it_works,
    reference,
    image_url,
    inventory_quantity,
    deferred_allowed,
    is_active,
    info_tags
  } = product;

  const getInventoryStatus = () => {
    if (inventory_quantity === undefined || inventory_quantity === null) return null;

    const isDeferredAllowed = deferred_allowed === undefined || deferred_allowed === null
      ? true
      : Boolean(deferred_allowed);

    const quantity = Number(inventory_quantity);
    if (Number.isNaN(quantity)) return null;

    if (quantity <= 0) {
      return isDeferredAllowed ? 'deferred' : 'out-of-stock';
    }

    return 'instant';
  };

  const inventoryStatus = getInventoryStatus();

  const isActive = is_active === undefined || is_active === null
    ? true
    : Boolean(is_active);

  const statusTags = [];

  if (inventoryStatus) {
    statusTags.push({
      key: 'inventory-status',
      type: inventoryStatus,
      label:
        inventoryStatus === 'instant'
          ? 'INSTANT'
          : inventoryStatus === 'deferred'
            ? 'DEFERRED'
            : 'OUT OF STOCK'
    });
  }

  if (isActive) {
    statusTags.push({
      key: 'active',
      type: 'active',
      label: 'ACTIVE'
    });
  } else {
    statusTags.push({
      key: 'inactive',
      type: 'inactive',
      label: 'INACTIVE'
    });
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        
        <div className="modal-details">
          <div className="modal-header">
            <div className="modal-title">
              <h2>{name}</h2>
              {Array.isArray(info_tags) && info_tags.length > 0 && (
                <div className="modal-info-tags">
                  {info_tags.map((tag) => (
                    <span key={tag} className="modal-info-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {statusTags.length > 0 && (
              <div className="modal-inventory-tags">
                {statusTags.map(tag => (
                  <div key={tag.key} className={`modal-inventory-tag ${tag.type}`}>
                    {tag.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-images">
            {image_url && image_url.length > 0 ? (
              image_url.map((url, index) => (
                <img 
                  key={index}
                  src={url} 
                  alt={`${name} ${index + 1}`} 
                  className="modal-image"
                  onError={(e) => {
                    e.target.src = "https://pub-22fd78ce20764889a8892ec465765dae.r2.dev/cdnimages/PLACEHOLDER.png";
                  }}
                />
              ))
            ) : (
              <img 
                src="https://pub-22fd78ce20764889a8892ec465765dae.r2.dev/cdnimages/PLACEHOLDER.png"
                alt={`${name} placeholder`} 
                className="modal-image"
              />
            )}
          </div>

          {/* Product ID and Item Code Info */}
          <div className="product-identifiers">
            <div className="identifier-item">
              <strong>Product ID:</strong> {product_id || 'N/A'}
            </div>
            <div className="identifier-item">
              <strong>Item Code:</strong> {product.item_code || product.dist_item_code || 'N/A'}
            </div>
            <div className="identifier-item">
              <strong>Inventory Quantity:</strong> {inventory_quantity !== undefined && inventory_quantity !== null ? inventory_quantity : 'N/A'}
            </div>
            {inventoryDetails && inventoryDetails.length > 0 && (
              <div className="identifier-item">
                <strong>Available Locations:</strong> {
                  inventoryDetails
                    .filter(item => item.quantity_available > 0)
                    .map(item => item.rack_location ? item.rack_location.join(', ') : 'N/A')
                    .filter(location => location !== 'N/A')
                    .join('; ') || 'No locations with stock'
                }
              </div>
            )}
          </div>

          {/* Data Comparison Table */}
          <div className="data-comparison-table">
            <h3>Product Data Comparison</h3>
            <div className="table-container">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Catalogue Data</th>
                    <th>Distributor Data</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Name</strong></td>
                    <td>{name || 'N/A'}</td>
                    <td>{distributorDetails?.product_name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Manufacturer</strong></td>
                    <td>{manufacturers || 'N/A'}</td>
                    <td>{distributorDetails?.manufacturer || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Distributor</strong></td>
                    <td>{product.distributor || 'N/A'}</td>
                    <td>{distributorDetails?.distributor || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>MRP</strong></td>
                    <td>₹{product.distributor_mrp || 'N/A'}</td>
                    <td>₹{distributorDetails?.mrp || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Package</strong></td>
                    <td>{packaging_detail || 'N/A'}</td>
                    <td>{distributorDetails?.package || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Plazza Selling Price (Incl. GST)</strong></td>
                    <td>₹{product.selling_price_incl_gst || 'N/A'}</td>
                    <td>₹{distributorDetails?.selling_price_incl_gst || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Effective Customer Discount</strong></td>
                    <td>{product.effective_customer_discount ? `${product.effective_customer_discount}%` : 'N/A'}</td>
                    <td>{distributorDetails?.effective_customer_discount ? `${distributorDetails.effective_customer_discount}%` : 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>GST</strong></td>
                    <td>{product.gst_rate ? `${product.gst_rate}%` : 'N/A'}</td>
                    <td>{distributorDetails?.gst_rate ? `${distributorDetails.gst_rate}%` : 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>HSN</strong></td>
                    <td>{product.hsn_code || 'N/A'}</td>
                    <td>{distributorDetails?.hsn_code || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Inventory Quantity</strong></td>
                    <td>{inventory_quantity !== undefined && inventory_quantity !== null ? inventory_quantity : 'N/A'}</td>
                    <td>N/A</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            {loadingDistributor && (
              <div className="loading-message">
                <p>Loading distributor details...</p>
              </div>
            )}
            
            {!loadingDistributor && !distributorDetails && (
              <div className="no-data-message">
                <p>No distributor details available for this product.</p>
              </div>
            )}
          </div>

          {/* Inventory Details Section */}
          <div className="inventory-details-section">
            <h3>Inventory Status Details</h3>
            {loadingInventory ? (
              <div className="loading-message">
                <p>Loading inventory details...</p>
              </div>
            ) : inventoryDetails && inventoryDetails.length > 0 ? (
              <div className="table-container">
                <table className="inventory-details-table">
                  <thead>
                    <tr>
                      <th>Batch Number</th>
                      <th>Quantity Available</th>
                      <th>Expiry Date</th>
                      <th>Status</th>
                      <th>Rack Location</th>
                      <th>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryDetails.map((item, index) => (
                      <tr key={item.id || index}>
                        <td>{item.batch_number || 'N/A'}</td>
                        <td>{item.quantity_available || 'N/A'}</td>
                        <td>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          <span className={`status-badge status-${item.status?.toLowerCase() || 'unknown'}`}>
                            {item.status || 'N/A'}
                          </span>
                        </td>
                        <td>{item.rack_location ? item.rack_location.join(', ') : 'N/A'}</td>
                        <td>{item.last_updated ? new Date(item.last_updated).toLocaleString() : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-data-message">
                <p>No inventory details available for this product.</p>
              </div>
            )}
          </div>

          {introduction && (
            <div>
              <h3>Introduction</h3>
              <p>{introduction}</p>
            </div>
          )}

          {benefits && (
            <div>
              <h3>Benefits</h3>
              <p>{benefits}</p>
            </div>
          )}

          {description && (
            <div>
              <h3>Description</h3>
              <p>{description}</p>
            </div>
          )}

          {how_to_use && (
            <div>
              <h3>How to Use</h3>
              <p>{how_to_use}</p>
            </div>
          )}

          {if_miss && (
            <div>
              <h3>If You Miss a Dose</h3>
              <p>{if_miss}</p>
            </div>
          )}

          {safety_advise && (
            <div>
              <h3>Safety Advice</h3>
              <p>{safety_advise}</p>
            </div>
          )}

          {fact_box && (
            <div>
              <h3>Fact Box</h3>
              <p>{fact_box}</p>
            </div>
          )}

          {primary_use && (
            <div>
              <h3>Primary Use</h3>
              <p>{primary_use}</p>
            </div>
          )}

          {storage && (
            <div>
              <h3>Storage</h3>
              <p>{storage}</p>
            </div>
          )}

          {common_side_effect && (
            <div>
              <h3>Common Side Effects</h3>
              <p>{common_side_effect}</p>
            </div>
          )}

          <div>
            <h3>Interactions</h3>
            <p><strong>Alcohol:</strong> {alcohol_interaction}</p>
            <p><strong>Pregnancy:</strong> {pregnancy_interaction}</p>
            <p><strong>Breastfeeding:</strong> {lactation_interaction}</p>
            <p><strong>Driving:</strong> {driving_interaction}</p>
            <p><strong>Kidney:</strong> {kidney_interaction}</p>
            <p><strong>Liver:</strong> {liver_interaction}</p>
          </div>

          {manufacturer_address && (
            <div>
              <h3>Manufacturer Address</h3>
              <p>{manufacturer_address}</p>
            </div>
          )}

          {how_it_works && (
            <div>
              <h3>How It Works</h3>
              <p>{how_it_works}</p>
            </div>
          )}

          {q_a && (
            <div>
              <h3>Q&A</h3>
              <p>{q_a}</p>
            </div>
          )}

          {reference && (
            <div>
              <h3>References</h3>
              <p>{reference}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductModal; 